export const name = 'gzFormFooterBar';

/**
 * @ngdoc directive
 * @name gazelle.components.formFooterBar.directive: gzFormFooterBar
 *
 * @param {string} submit-title (optional) Property string to provide as a custom label for the submit button. By default,
 *                              a localized version of 'SUBMIT' is provided.
 * @param {boolean} show-cancel (optional) Flag to indicate if the cancel button should be shown. Defaults to true.
 * @param {boolean} submitting (required) Flag to indicate the form is currently being submitted.
 * @param {function} disable-submit (optional) Custom function for evaluating the conditions for when the submit button is
 *                                  disabled. By default, a function is provided which will disable the submit button
 *                                  while the form is invalid or while the form is in a submitting state.
 * @param {function} cancel (optional) A function to perform a custom cancellation process. If this is provided the developer
 *                          becomes responsible for closing the dialog. If this is not provided the dialog will be closed
 *                          by gzFormTitleBar.
 * @param {function} show-legend (optional) Flag to indicate if the required legend should be shown.  Defaults to true.
 * @param {function} alt-background (optional) Flag to indicate alternate background color should be shown.
 *
 * @description Provides a consistent look for form footers with built-in L10 property string conversion and a default
 *              submit button title, cancel button function, and submit button function. The only required attribute is
 *              the <i>submitting</i> attribute which lets the directive know when the form is in a submitting state.
 *
 *              Left-aligned in the footer is a legend indicating that a star identifies a required field. Right-aligned
 *              in the footer are the cancel and submit buttons, in that order.
 *
 * @usage
 * ````html
 *
 * // With default submit title, submit function, and cancel function
 * <gz-form-footer-bar submitting="vm.sending"></gz-form-footer-bar>
 *
 * // With custom submit title
 * <gz-form-footer-bar submitting="vm.sending"
 *                     submit-title="account.account.Some_Property_Key"></gz-form-footer-bar>
 *
 * // With custom disable submit function
 * <gz-form-footer-bar submitting="vm.sending"
 *                     disable-submit="vm.myDisableSubmitFunction"></gz-form-footer-bar>
 *
 * // With custom cancel function
 * <gz-form-footer-bar submitting="vm.sending"
 *                     cancel="vm.myCancelFunction"></gz-form-footer-bar>
 * ````
 */

export default function gzFormFooterBar() {
  //noinspection BadExpressionStatementJS
  'ngInject';

  let directive = {
    require: '^?form',   /// this is not really optional, however, Jasmine can't find it and throws an error
    restrict: 'E',
    scope: {
      submitTitle: '@',
      submitting: '=',
      showCancel: '=?',
      disableSubmit: '=?',
      cancel: '=?',
      showLegend: '=?',
      altBackground: '=?',
      legendKey: '@'
    },
    template: require('./form-footer-bar.jade'),
    controllerAs: 'vm',
    bindToController: true,
    link: gzFormFooterBarLink,
    controller: gzFormFooterBarController
  };

  return directive;

  ////////////////////

  function gzFormFooterBarLink(scope, iElem, iAttrs, form) {

    scope.$watch(
      () => {
        return form ? form.$invalid : true;
      },
      (newVal) => {
        if (newVal !== undefined) {
          scope.vm.setInvalidForm(newVal);
        }
      }
    );
  }

  /** @ngInject **/
  function gzFormFooterBarController($mdDialog, $parse) {

    let vm = this;

    vm.invalidForm = true;

    vm.defaultCancel = defaultCancel;
    vm.defaultDisableSubmit = defaultDisableSubmit;
    vm.setInvalidForm = setInvalidForm;

    vm.$onInit = onInit;

    ////////////////////

    function onInit() {
      if (vm.showLegend === undefined) {
        vm.showLegend = true;
      }

      // Don't show the cancel button if it isn't wanted
      if (vm.showCancel === undefined) {
        vm.showCancel = true;
      }

      // If a custom submit label is not provided use the default label
      if (vm.submitTitle === undefined) {
        vm.submitTitle = 'uxf.uxf.Dialog_Submit';
      }

      // If a custom disable submit function is not provided use the default  disable submit function
      if (vm.disableSubmit === undefined) {
        vm.disableSubmit = defaultDisableSubmit;
      }

      // If a custom cancel function is not provided assign the default cancel function
      if (vm.cancel === undefined) {
        vm.cancel = defaultCancel;
      }
    }

    function defaultCancel() {
      $mdDialog.cancel();
    }

    function defaultDisableSubmit() {
      return vm.submitting || vm.invalidForm;
    }

    function setInvalidForm(valid) {
      vm.invalidForm = valid;
    }
  }
}
